import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Shield, Lock, Database, Mail, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const lastUpdated = "February 10, 2026";

  return (
    <div className="min-h-screen">
      <Navigation />

      <main className="pt-16">
        {/* Hero */}
        <section className="py-16 sm:py-20 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto text-center relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Your privacy matters
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              How The Tech Bus collects, uses, and protects your information.
            </p>
            <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="container mx-auto max-w-3xl prose prose-neutral dark:prose-invert">
            <div className="space-y-10">
              <div>
                <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
                  <Database className="w-6 h-6 text-primary" />
                  1. Information We Collect
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We collect information you provide directly, such as when you contact us, subscribe to updates, or use our services. This may include your name, email address, company name, and the content of your messages.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  We also automatically collect certain technical data when you visit our website, including your IP address, browser type, device information, and how you interact with our pages. This helps us improve our site and services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-primary" />
                  2. How We Use Your Information
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Send you updates about our services, with your consent</li>
                  <li>Improve our website, products, and user experience</li>
                  <li>Analyze usage patterns and troubleshoot issues</li>
                  <li>Comply with legal obligations and protect our rights</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">3. Information Sharing</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We do not sell your personal information. We may share your data only with trusted service providers who assist us in operating our website and business (e.g., hosting, analytics, email delivery), and only under strict confidentiality agreements. We may also disclose information when required by law or to protect our rights and safety.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">4. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments. While we strive to protect your data, no method of transmission over the internet is 100% secure.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">5. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  Depending on your location, you may have the right to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction or deletion of your data</li>
                  <li>Object to or restrict certain processing</li>
                  <li>Data portability</li>
                  <li>Withdraw consent where we rely on it</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  To exercise these rights, please contact us at{" "}
                  <a href="mailto:support@thetechbus.com" className="text-primary hover:underline">
                    support@thetechbus.com
                  </a>
                  .
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">6. Cookies and Tracking</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website may use cookies and similar technologies to enhance your experience, remember your preferences, and analyze traffic. You can control cookie settings through your browser. Disabling certain cookies may affect the functionality of our site.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold mb-4">7. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="pt-8 border-t border-border">
                <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-primary" />
                  Contact Us
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have questions about this Privacy Policy or our practices, please contact us at{" "}
                  <a href="mailto:support@thetechbus.com" className="text-primary hover:underline">
                    support@thetechbus.com
                  </a>
                  . We are based in Gurugram, Haryana, India.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 mt-4 text-primary font-medium hover:underline"
                >
                  Go to Contact page
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
